/**
 * Cross-product shared-session helper.
 *
 * Sourcing and Supplier Portal share one auth backend and one user identity.
 * localStorage is origin-scoped and cannot be read across the two subdomains, so
 * the cross-product source of truth is a cookie scoped to `Domain=.mintmesh.ai`,
 * which every *.mintmesh.ai host can read.
 *
 * This module replicates the exact contract the Supplier Portal already uses so
 * the two apps can read each other's cookie byte-for-byte:
 *   name   : <tenant>_mm_session  (legacy: mm_session -- see below)
 *   domain : .mintmesh.ai
 *   path   : /
 *   flags  : Secure; SameSite=Lax
 *   value  : encodeURIComponent( base64( JSON.stringify({ token, user }) ) )
 *            base64 is UTF-8 safe: btoa(unescape(encodeURIComponent(json)))
 *
 * TENANT SCOPING
 * A single domain-wide `mm_session` is visible to every tenant host under
 * mintmesh.ai, so a token minted for tenant A was being adopted on tenant B and
 * only rejected after a backend round trip. The cookie name is therefore keyed
 * by the tenant label taken from the hostname (`qa.sourcing.mintmesh.ai` ->
 * `qa_mm_session`), so a foreign tenant's session is skipped locally instead of
 * adopted and verified.
 *
 * This does NOT change the browser's isolation boundary: Domain is still
 * .mintmesh.ai, so tenant B's page still *receives* `a_mm_session` in
 * document.cookie. What it removes is the accidental adoption (and its round
 * trip). Real isolation would need a narrower Domain, i.e. a DNS layout where
 * both products share a per-tenant parent.
 *
 * Because Supplier Portal reads this cookie too, the derivation below must stay
 * byte-for-byte identical on both sides. During a staggered rollout the legacy
 * domain-wide `mm_session` is still read as a fallback and re-keyed under the
 * tenant name on the next write; set VUE_APP_SHARED_SESSION_LEGACY_FALLBACK to
 * 'false' once both products ship the tenant-scoped name, to close the window
 * where a legacy cookie can still cross tenants.
 *
 * All cookie logic is gated on isSharedDomain(): on localhost / non-mintmesh
 * hosts this is a no-op and the app falls back to its existing local storage.
 */

const SHARED_COOKIE_SUFFIX = 'mm_session';
// The pre-tenant-scoping cookie name, shared by every host under the domain.
const LEGACY_SHARED_COOKIE = 'mm_session';
// One-shot hand-off token dropped by the IdP round trip and consumed by ssoLogin().
const SSO_HANDOFF_COOKIE = 'auth_jwt';
const SHARED_DOMAIN = '.mintmesh.ai';
// seconds; configurable via env, default 7 days
const MAX_AGE = Number(process.env.VUE_APP_SHARED_SESSION_MAX_AGE) || 7 * 24 * 60 * 60;
const MAX_COOKIE_BYTES = 3800; // ~4KB cookie cap guard (name + value)

function legacyFallbackEnabled() {
  return String(process.env.VUE_APP_SHARED_SESSION_LEGACY_FALLBACK) !== 'false';
}

export function isSharedDomain() {
  try {
    return window.location.hostname.endsWith('mintmesh.ai');
  } catch (e) {
    return false;
  }
}

/**
 * Tenant label for the current host: the first hostname label, sanitised to the
 * cookie-name-safe charset. Returns '' when the host has no tenant label of its
 * own (bare `mintmesh.ai`, `localhost`), in which case the legacy name is used.
 * Derived from the URL only, so it is available synchronously at boot -- before
 * any API call, which is what lets us decide whether to adopt the cookie.
 */
export function tenantKey() {
  try {
    const parts = String(window.location.hostname || '').split('.');
    if (parts.length < 3) return '';
    return parts[0].replace(/[^a-z0-9-]/gi, '').toLowerCase();
  } catch (e) {
    return '';
  }
}

/** Cookie name for this tenant, e.g. `qa_mm_session`. */
export function sharedCookieName() {
  const tenant = tenantKey();
  return tenant ? `${tenant}_${SHARED_COOKIE_SUFFIX}` : LEGACY_SHARED_COOKIE;
}

export function encodePayload(obj) {
  return encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify(obj)))));
}

export function decodePayload(raw) {
  try {
    return JSON.parse(decodeURIComponent(escape(atob(decodeURIComponent(raw)))));
  } catch (e) {
    return null;
  }
}

function getCookie(name) {
  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (let i = 0; i < parts.length; i++) {
    if (parts[i].startsWith(prefix)) return parts[i].slice(prefix.length);
  }
  return null;
}

function setCookie(name, value, maxAge) {
  let c = `${name}=${value}; Domain=${SHARED_DOMAIN}; Path=/; SameSite=Lax; Secure`;
  if (typeof maxAge === 'number') c += `; Max-Age=${maxAge}`;
  document.cookie = c;
}

function deleteCookie(name) {
  document.cookie = `${name}=; Domain=${SHARED_DOMAIN}; Path=/; Max-Age=0; SameSite=Lax; Secure`;
}

/**
 * Read the shared cross-product session for THIS tenant, if present on a shared
 * domain. Falls back to the legacy domain-wide cookie while the rollout is in
 * flight; the fallback value is re-keyed under the tenant name by the next
 * writeSession (every boot-time adopt path runs one), not here -- a reader must
 * not mutate cookies, since this is called on every focus/visibility change.
 * @returns {{ token: string, user: (object|null) } | null}
 */
export function readCookieSession() {
  if (!isSharedDomain()) return null;
  const name = sharedCookieName();
  let raw = getCookie(name);
  if (!raw && name !== LEGACY_SHARED_COOKIE && legacyFallbackEnabled()) {
    raw = getCookie(LEGACY_SHARED_COOKIE);
  }
  if (!raw) return null;
  const p = decodePayload(raw);
  if (!p || !p.token) return null;
  return { token: p.token, user: p.user != null ? p.user : null };
}

/**
 * Publish the session to this tenant's shared cookie so the other product can
 * adopt it. No-op off a shared domain (localhost/dev keep local-storage-only
 * behaviour). If the encoded value would exceed the cookie cap, drop `user` and
 * store the token only (the other side then has the token but no user object for
 * RBAC). Also drops the legacy domain-wide cookie: once a tenant-scoped cookie
 * exists, the legacy one is only a cross-tenant leak.
 */
export function writeSession(token, user) {
  if (!isSharedDomain()) return;
  if (!token) return;
  const name = sharedCookieName();
  let value = encodePayload({ token, user });
  if (value.length + name.length > MAX_COOKIE_BYTES) {
    value = encodePayload({ token, user: null });
  }
  setCookie(name, value, MAX_AGE);
  if (name !== LEGACY_SHARED_COOKIE) {
    deleteCookie(LEGACY_SHARED_COOKIE);
  }
}

/**
 * Remove the shared cookie (single logout across products). Clears the legacy
 * name too -- leaving it behind would let a stale domain-wide cookie be adopted
 * again at the next boot for up to MAX_AGE. No-op off shared domain.
 */
export function clearSharedSession() {
  if (!isSharedDomain()) return;
  const name = sharedCookieName();
  deleteCookie(name);
  if (name !== LEGACY_SHARED_COOKIE) {
    deleteCookie(LEGACY_SHARED_COOKIE);
  }
}

/**
 * Drop the SSO hand-off token. It is single-use: once the /ssologin exchange has
 * been attempted the cookie is spent, so both the success path and every failure
 * path must clear it. Left behind, the login page re-attempts the same dead
 * exchange on each mount -- and a 401 there trips the interceptor's logout +
 * reload, which lands back on the login page and retries it again.
 *
 * Cleared at both scopes: the IdP sets it on `.mintmesh.ai`, but a host-scoped
 * copy would shadow the domain one and keep the loop alive. Deliberately no
 * `Secure` flag -- an expiry the browser refuses to write over plain HTTP is
 * worse than a non-secure one, and the value is being discarded either way.
 */
export function clearSsoHandoffCookie() {
  const expire = `${SSO_HANDOFF_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
  document.cookie = expire;
  if (isSharedDomain()) {
    document.cookie = `${expire}; Domain=${SHARED_DOMAIN}`;
  }
}

/**
 * Cookie-first token accessor: prefer a token refreshed by the other product,
 * falling back to this app's own local token cookie (mm_auth_key).
 */
export function getToken() {
  if (isSharedDomain()) {
    const s = readCookieSession();
    if (s && s.token) return s.token;
  }
  return getCookie('mm_auth_key');
}
